import { defineMessages, MessageDescriptor } from 'react-intl';
import { SocialPostFormatHint, SocialPostFormatName } from 'fym-shared/src/music24/socialPostFormat';
import { ComposerFormatRefusal } from './composerFormatLine';

const labels = defineMessages({
  reel: { id: 'app.sml.promote.composer.formatLine.reel', defaultMessage: 'Goes out as a Reel' },
  short: { id: 'app.sml.promote.composer.formatLine.short', defaultMessage: 'Goes out as a Short' },
  video: { id: 'app.sml.promote.composer.formatLine.video', defaultMessage: 'Goes out as a video' },
  photo: { id: 'app.sml.promote.composer.formatLine.photo', defaultMessage: 'Goes out as a photo' },
  text: { id: 'app.sml.promote.composer.formatLine.text', defaultMessage: 'Goes out as a text post' },
  story: { id: 'app.sml.promote.composer.formatLine.story', defaultMessage: 'Goes out as a story' },
});

/**
 * The label of each format. `resolvePostFormat` never gives `carousel` (Music24 publishes one
 * file per post), so a carousel reads as a photo post if a rule gives it in the future.
 */
export const composerFormatLabels: Record<SocialPostFormatName, MessageDescriptor> = {
  ...labels,
  carousel: labels.photo,
};

export const composerFormatCannot = defineMessages({
  label: { id: 'app.sml.promote.composer.formatLine.cannot', defaultMessage: 'Cannot go out' },
});

/** The hint names the limit and the result. */
export const composerFormatHints: Record<SocialPostFormatHint, MessageDescriptor> = defineMessages({
  tooLongForReel: {
    id: 'app.sml.promote.composer.formatLine.hint.tooLongForReel',
    defaultMessage: 'Longer than 90 s: goes out as a video, not a Reel',
  },
  tooShortForReel: {
    id: 'app.sml.promote.composer.formatLine.hint.tooShortForReel',
    defaultMessage: 'Shorter than 3 s: goes out as a video, not a Reel',
  },
  notVerticalForReel: {
    id: 'app.sml.promote.composer.formatLine.hint.notVerticalForReel',
    defaultMessage: 'Wider than tall: goes out as a video, not a Reel',
  },
  tooLongForShort: {
    id: 'app.sml.promote.composer.formatLine.hint.tooLongForShort',
    defaultMessage: 'Longer than 3 min: goes out as a video, not a Short',
  },
  notVerticalForShort: {
    id: 'app.sml.promote.composer.formatLine.hint.notVerticalForShort',
    defaultMessage: 'Wider than tall: goes out as a video, not a Short',
  },
  // `resolvePostFormat` gives one code for Facebook and YouTube. The line picks the words below.
  unknownMeasures: {
    id: 'app.sml.promote.composer.formatLine.hint.unknownMeasures.reel',
    defaultMessage: 'We could not measure this file: goes out as a video, not a Reel',
  },
});

export const composerFormatUnknownForShort = defineMessages({
  hint: {
    id: 'app.sml.promote.composer.formatLine.hint.unknownMeasures.short',
    defaultMessage: 'We could not measure this file: goes out as a video, not a Short',
  },
});

/** Why the post cannot go out. `{platform}` is the brand name. */
export const composerFormatRefusals: Record<ComposerFormatRefusal, MessageDescriptor> = defineMessages({
  noStories: {
    id: 'app.sml.promote.composer.formatLine.refusal.noStories',
    defaultMessage: '{platform} has no stories.',
  },
  storyNeedsMedia: {
    id: 'app.sml.promote.composer.formatLine.refusal.storyNeedsMedia',
    defaultMessage: 'A story needs a photo or a clip.',
  },
  needsVideo: {
    id: 'app.sml.promote.composer.formatLine.refusal.needsVideo',
    defaultMessage: '{platform} needs a video.',
  },
  needsMedia: {
    id: 'app.sml.promote.composer.formatLine.refusal.needsMedia',
    defaultMessage: '{platform} needs a photo or a video.',
  },
  // Instagram only today (fym-shared `postImageRules.ts`). `{ratio}` is the shape of the image.
  imageAspect: {
    id: 'app.sml.promote.composer.formatLine.refusal.imageAspect',
    defaultMessage: '{platform} takes images from 4:5 to 1.91:1. This one is {ratio}.',
  },
  // fym-shared `postVideoRules.ts`. `{limit}` is "64 MB", "1 GB", "3 s" or "15 min".
  videoSize: {
    id: 'app.sml.promote.composer.formatLine.refusal.videoSize',
    defaultMessage: '{platform} takes videos of at most {limit}.',
  },
  videoTooShort: {
    id: 'app.sml.promote.composer.formatLine.refusal.videoTooShort',
    defaultMessage: '{platform} takes videos of at least {limit}.',
  },
  videoTooLong: {
    id: 'app.sml.promote.composer.formatLine.refusal.videoTooLong',
    defaultMessage: '{platform} takes videos of at most {limit}.',
  },
  videoAspect: {
    id: 'app.sml.promote.composer.formatLine.refusal.videoAspect',
    defaultMessage: '{platform} does not take a video of the shape {ratio}.',
  },
});
